import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import cloneDeep from "lodash-es/cloneDeep";

// API's
import { getPresignedPayPlanPDF } from "@/PayPlans/api/pdf/queries";

// MUI
import { Box } from "@mui/material";

// Components
import { SLSAPayPlanLayerReloader } from "@/PayPlans/components/Html/SLSAPayPlanLayerReloader";
import { PdfViewer } from "@/Layouts/components/PdfViwer/pdf-viewer";

//Icons
import { useServices } from "@/PayPlans/hooks/useServices/useServices";

//Show PDF
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { getMappedPosition } from "@/Common/api/positions/queries";
import { isMutablePosition } from "@/Common/helpers/Positions";

export default function PdfPayPlanLayer(props) {
	const { selected, payPlan = null, isMyPayPlan = false, assignment = false } = props || {};

	const { _id, position } = selected || {};
	const positionMutable = isMutablePosition(position);
	const location = useSelector((state) => state.location.location);
	let documentType = "pending-signature";
	let documentName = isMyPayPlan ? `${selected?.title}.pdf` : `${selected?.name}.pdf`;
	// Check if the acknowledgement for selected pay plan is completed
	const acknowledgementCompleted =
		selected?.acknowledgementStatus?.length > 0 &&
		selected?.acknowledgementStatus?.[selected?.acknowledgementStatus?.length - 1]?.status === "COMPLETED";
	//Change document type and name if the acknowledgement is completed
	if (checkIsMyPayPlan({ isMyPayPlan, selected, acknowledgementCompleted })) {
		documentType = "signed";
		documentName = selected?.acknowledgementDocumentLink ?? `${selected?.title}.pdf`;
	}

	if (assignment) {
		documentName = `${selected?.planId?.name}.pdf`;
	}

	//Call to get mapped position
	const { data: dataMappedPosition, isLoading: isLoadingMappedPosition } = useQuery({
		queryKey: ["mappedPosition", "getMappedPosition", location?.locationId, position],
		queryFn: async () => getMappedPosition(location?.locationId, position),
		enabled: enabledMappedPosition({ location, positionMutable }),
		staleTime: Infinity,
	});

	const { queries } = useServices(position, getUserModality({ dataMappedPosition, positionMutable }));
	const { data: pdfLinkData, isLoading: pdfLinkLoading } = useQuery({
		queryKey: ["htmlPayPlanView", _id, location?.locationId],
		queryFn: async () => {
			let res;
			if (payPlan) {
				res = cloneDeep(payPlan);
			} else {
				res = await queries.getPayPlan(location?.locationId, _id);
			}

			const pdfLink = await getPresignedPayPlanPDF(location?.locationId, res?.position, documentType, documentName);
			res.pdfLink = pdfLink?.presignedURL || null;
			return res;
		},
		staleTime: Infinity,
		enabled: enabledPresignedURL({ props, location, positionMutable, dataMappedPosition }),
	});

	return (
		<>
			{pdfLinkLoading || isLoadingMappedPosition ? (
				<SLSAPayPlanLayerReloader />
			) : (
				<Box maxHeight={700} overflow="auto" className="!w-full">
					<PdfViewer fileName={`${pdfLinkData?.name}.pdf`} file={pdfLinkData?.pdfLink} isMyPayPlan={isMyPayPlan} />
				</Box>
			)}
		</>
	);
}

function enabledMappedPosition({ location, positionMutable }) {
	return !!location?.locationId && positionMutable;
}

function enabledPresignedURL({ props, location, positionMutable, dataMappedPosition }) {
	return !!props?.selected?._id && !!location?.locationId && (!positionMutable || !!dataMappedPosition);
}

function getUserModality({ dataMappedPosition, positionMutable }) {
	return dataMappedPosition?.isDepartmental || !positionMutable ? "Departmental" : "Producer";
}

function checkIsMyPayPlan({ isMyPayPlan, selected, acknowledgementCompleted }) {
	return isMyPayPlan && selected?.acknowledgementDocumentLink?.length > 0 && acknowledgementCompleted;
}

PdfPayPlanLayer.propTypes = {
	payPlan: PropTypes.object,
	selected: PropTypes.shape({
		_id: PropTypes.string,
		position: PropTypes.string,
	}),
	model: PropTypes.object,
	isMyPayPlan: PropTypes.bool,
};
